import React, { useRef, useState } from 'react';
import EverTrue from 'app';
import PropTypes from 'prop-types';
import { Button, Icon, Popover2 } from '@evertrue/et-components';
import { RecorderProvider } from './hooks/recorder-context';
import UploadVideo from './upload-video';
import SignalVideoController from './controllers/signal-video-controller';
import { ThankviewSettingsProvider } from './hooks/thankview-settings-context';
import useVideoProcessing from './hooks/use-video-processing';
import { useSignalVideoModalContext } from 'apps/signal-video/contexts/use-signal-video-modal-context';
import { SignalVideoModalProvider } from 'apps/signal-video/contexts/use-signal-video-modal-context';
import { ThankViewAuthProvider } from 'apps/signal-video/contexts/thankview-auth-context';

const ThankviewCTA = ({ popoverTrigger, onCopyVideo = () => {} }) => {
  const uploadVideoRef = useRef(null);
  const [showDefaultSettingsModal, setShowDefaultSettingsModal] = useState(
    !localStorage.getItem('has_updated_signal_video_settings')
  );

  const { uploadVideo, uploadVideoFromFile, processingVideoState } = useVideoProcessing();
  const { handleOpenThankviewModal, isThankviewModalOpen } = useSignalVideoModalContext();

  const handleUploadClick = async () => {
    try {
      EverTrue.track.set('signal_video', { type: 'signal_video_upload' });
      uploadVideoRef.current.triggerFileInput();
    } catch (error) {
      EverTrue.Alert.error('Error uploading video');
    }
  };
  const menuItems = [
    {
      id: 'record',
      label: 'Record New Video',
      onClick: () => {
        if (showDefaultSettingsModal) {
          handleOpenThankviewModal('settings');
        } else {
          handleOpenThankviewModal('recorder');
        }
        EverTrue.track.set('signal_video', { type: 'signal_video_create' });
      },
      icon: 'tv-video',
      iconKit: 'tv',
    },
    {
      id: 'library',
      label: 'Video Library',
      onClick: () => {
        if (showDefaultSettingsModal) {
          handleOpenThankviewModal('settings');
        } else {
          handleOpenThankviewModal('library');
        }
        EverTrue.track.set('signal_video', { type: 'signal_video_library' });
      },
      icon: 'video-library',
      iconKit: 'tv',
    },
    {
      id: 'upload-video',
      label: 'Upload Video',
      onClick: () => {
        if (showDefaultSettingsModal) {
          handleOpenThankviewModal('settings', 'uploadVideo');
        } else {
          handleUploadClick();
        }
      },
      icon: 'tv-upload',
      iconKit: 'tv',
    },
  ];
  const handleFileSelect = (file) => {
    handleOpenThankviewModal('recorder');
  };

  return (
    <>
      <Popover2
        trigger={({ togglePopover }) => {
          if (popoverTrigger) {
            return React.cloneElement(popoverTrigger, { onClick: togglePopover });
          }
          return (
            <Button
              className="et--button-secondary tv-cta-button--margin"
              onClick={() => {
                EverTrue.track.set('signal_video', { type: 'signal_video_open' });
                togglePopover();
              }}
            >
              <Icon icon="tv-video" iconKit="tv" />
            </Button>
          );
        }}
      >
        {({ closePopover }) => (
          <div className="tv-dropdown-content">
            {menuItems.map((item) => (
              <a
                key={item.label}
                onClick={() => {
                  item.onClick();
                  closePopover();
                }}
                className="tv-dropdown-item"
              >
                {item.icon && <Icon icon={item.icon} iconKit={item.iconKit} className="tv-dropdown-item-icon" />}
                <span className="tv-dropdown-item-label">{item.label}</span>
              </a>
            ))}
          </div>
        )}
      </Popover2>
      <RecorderProvider>
        <ThankviewSettingsProvider>
          <UploadVideo ref={uploadVideoRef} onFileSelect={handleFileSelect} uploadVideoFromFile={uploadVideoFromFile} />
          {isThankviewModalOpen && (
            <SignalVideoController
              uploadVideo={uploadVideo}
              processingVideoState={processingVideoState}
              onCopyVideo={onCopyVideo}
              setShowDefaultSettingsModal={setShowDefaultSettingsModal}
              handleUploadClick={handleUploadClick}
            />
          )}
        </ThankviewSettingsProvider>
      </RecorderProvider>
    </>
  );
};

ThankviewCTA.propTypes = {
  popoverTrigger: PropTypes.node,
  onCopyVideo: PropTypes.func,
};

const WrappedThankviewCTA = ({ popoverTrigger, onCopyVideo }) => (
  <SignalVideoModalProvider>
    <ThankViewAuthProvider>
      <ThankviewCTA popoverTrigger={popoverTrigger} onCopyVideo={onCopyVideo} />
    </ThankViewAuthProvider>
  </SignalVideoModalProvider>
);

WrappedThankviewCTA.propTypes = {
  popoverTrigger: PropTypes.node,
  onCopyVideo: PropTypes.func,
};

export default WrappedThankviewCTA;
